import { useCallback, useContext } from 'react';
import { ActivitiesApiContext, SearchTaskRequest } from '@urbanx/agx.activities.client';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { TaskFilterState } from './TaskFilters/TaskFiltersState';
import { createTaskSearchQueryKey } from './taskSearchQuery';

const PAGE_SIZE = 20;

const createApiSafeArray = <T>(arr?: T[]) => {
  return arr === undefined || arr.length === 0 ? undefined : arr;
};

const buildSearchRequest = (
  taskFilters: TaskFilterState,
  continuationToken?: string,
  fresh?: boolean
): SearchTaskRequest => ({
  pageSize: PAGE_SIZE,
  continuationToken: fresh ? undefined : continuationToken,
  requireQueryCount: fresh,
  states: createApiSafeArray(taskFilters.states),
  teamTypes: createApiSafeArray(taskFilters.teamTypes),
  stages: createApiSafeArray(taskFilters.stages),
  agencyIds: createApiSafeArray(taskFilters.agencyIds),
  assignedToUserIds: createApiSafeArray(taskFilters.assignedToUserIds),
  taskDue: taskFilters.taskDue,
  addressSearchText: taskFilters.addressSearchText,
});

const useTaskSearch = (
  taskFilters?: TaskFilterState,
  filtersPending?: boolean,
  hasNoMatchingAgencies?: boolean,
  searchBlocked?: boolean
) => {
  const api = useContext(ActivitiesApiContext);
  const queryClient = useQueryClient();
  const queryKey = createTaskSearchQueryKey(taskFilters);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isSuccess } =
    useInfiniteQuery({
      queryKey,
      initialPageParam: undefined as string | undefined,
      enabled: !!taskFilters && !filtersPending && !hasNoMatchingAgencies && !searchBlocked,
      queryFn: ({ pageParam, signal }) => {
        if (!taskFilters) throw new Error('Task filters are required');

        return api.searchAllTasks(
          buildSearchRequest(taskFilters, pageParam, pageParam === undefined),
          signal
        );
      },
      getNextPageParam: (lastPage) => lastPage.continuationToken,
    });

  const refreshSearch = useCallback(() => {
    return queryClient.refetchQueries(
      {
        queryKey: createTaskSearchQueryKey(taskFilters),
        exact: true,
        type: 'active',
      },
      { throwOnError: true }
    );
  }, [queryClient, taskFilters]);

  const refreshSearchInBackground = useCallback(() => {
    return queryClient.refetchQueries(
      {
        queryKey: createTaskSearchQueryKey(taskFilters),
        exact: true,
        type: 'active',
      },
      { throwOnError: false }
    );
  }, [queryClient, taskFilters]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const searchUnavailable = filtersPending || hasNoMatchingAgencies || searchBlocked;
  const tasks = searchUnavailable ? [] : (data?.pages.flatMap((page) => page.tasks ?? []) ?? []);

  return {
    hasMore: !searchUnavailable && !!hasNextPage,
    totalCount:
      hasNoMatchingAgencies || searchBlocked
        ? 0
        : filtersPending
          ? undefined
          : data?.pages[0]?.totalCount,
    tasks,
    searchReady: !searchUnavailable && isSuccess,
    searchAllTasksPending: !searchUnavailable && (isPending || isFetchingNextPage),
    loadMore,
    refreshSearch,
    refreshSearchInBackground,
  };
};

export default useTaskSearch;
